import { getBattleMoveDuration } from "../../core/constants.js";

export const BLANK_ANIMATION_FRAME = "blank";

export function getAnimationRangeFrameCount(range = null) {
  if (!range) {
    return 0;
  }

  return Math.max(0, range.end - range.start + 1);
}

export function getAnimationRangeFrameIndices(range = null, { reverse = false } = {}) {
  const frameCount = getAnimationRangeFrameCount(range);

  if (frameCount <= 0) {
    return [];
  }

  const frames = Array.from({ length: frameCount }, (_, index) => range.start + index);
  return reverse ? frames.reverse() : frames;
}

export function getAnimationFrameSequence(animationSpec, rangeName = "default", range = null) {
  const explicitSequence =
    animationSpec?.frameSequences?.[rangeName] ??
    animationSpec?.frameSequences?.default ??
    null;

  if (Array.isArray(explicitSequence) && explicitSequence.length > 0) {
    return [...explicitSequence];
  }

  return getAnimationRangeFrameIndices(range ?? getAnimationRange(animationSpec, rangeName));
}

export function getAnimationPlaybackDurationMs(frameCount = 0, frameRate = 1) {
  if (frameCount <= 0) {
    return 0;
  }

  return Math.max(1, Math.round((frameCount / Math.max(1, frameRate)) * 1000));
}

export function getAnimationRange(animationSpec, rangeName = "default") {
  return animationSpec?.ranges?.[rangeName] ?? animationSpec?.ranges?.default ?? null;
}

export function getOwnerIdleFlipX(owner = "player") {
  return owner === "enemy";
}

export function getUnitAttackRangeName(owner = "player", directionX = 0) {
  if (directionX > 0) {
    return "right";
  }

  if (directionX < 0) {
    return "left";
  }

  return owner === "enemy" ? "left" : "right";
}

export function getWalkAnimationPlayback(
  owner,
  walkAnimation,
  directionX = 0,
  directionY = 0,
) {
  if (!walkAnimation?.key) {
    return null;
  }

  const ranges = walkAnimation.ranges ?? {};
  const ownerFlipX = getOwnerIdleFlipX(owner);
  let requestedRangeName = "default";

  if (directionY < 0) {
    requestedRangeName = "up";
  } else if (directionY > 0) {
    requestedRangeName = "down";
  } else if (directionX > 0) {
    requestedRangeName = "right";
  } else if (directionX < 0) {
    requestedRangeName = "left";
  }

  if (ranges[requestedRangeName]) {
    return {
      rangeName: requestedRangeName,
      range: ranges[requestedRangeName],
      startFrame: ranges[requestedRangeName].start,
      flipX: requestedRangeName === "default" ? ownerFlipX : false,
    };
  }

  if (requestedRangeName === "left" && ranges.right) {
    return {
      rangeName: "right",
      range: ranges.right,
      startFrame: ranges.right.start,
      flipX: true,
    };
  }

  if (requestedRangeName === "right" && ranges.left) {
    return {
      rangeName: "left",
      range: ranges.left,
      startFrame: ranges.left.start,
      flipX: true,
    };
  }

  if (!ranges.default) {
    return null;
  }

  return {
    rangeName: "default",
    range: ranges.default,
    startFrame: ranges.default.start,
    flipX: ownerFlipX,
  };
}

export function getUnitDefaultTexture(visualSpec, owner = "player") {
  const idleRange = getAnimationRange(visualSpec?.idle, "default");

  if (visualSpec?.idle?.key && idleRange) {
    return {
      key: visualSpec.idle.key,
      frame: idleRange.start,
      flipX: getOwnerIdleFlipX(owner),
    };
  }

  if (visualSpec?.fallbackKey) {
    return {
      key: visualSpec.fallbackKey,
      frame: undefined,
      flipX: getOwnerIdleFlipX(owner),
    };
  }

  return null;
}

export function getAttackAnimationPlayback(owner, attackAnimation, directionX = 0) {
  const requestedRangeName = getUnitAttackRangeName(owner, directionX);
  const oppositeRangeName = requestedRangeName === "left" ? "right" : "left";
  let rangeName = requestedRangeName;
  let range = getAnimationRange(attackAnimation, rangeName);
  let flipX = false;

  if (!range) {
    const oppositeRange = getAnimationRange(attackAnimation, oppositeRangeName);

    if (oppositeRange) {
      rangeName = oppositeRangeName;
      range = oppositeRange;
      flipX = true;
    } else {
      rangeName = "default";
      range = getAnimationRange(attackAnimation, "default");
    }
  }

  if (!attackAnimation?.key || !range) {
    return null;
  }

  const frameSequence = getAnimationFrameSequence(attackAnimation, rangeName, range);

  return {
    rangeName,
    range,
    startFrame: range.start,
    frameSequence,
    flipX,
    durationMs: getAnimationPlaybackDurationMs(
      frameSequence.length,
      attackAnimation.frameRate,
    ),
  };
}

export function getUnitMovementPlayback(visualSpec, moveSegments = 0) {
  const walkAnimation = visualSpec?.walk ?? null;
  const defaultRange = getAnimationRange(walkAnimation, "default");
  const forwardFrameIndices = getAnimationRangeFrameIndices(defaultRange);
  const canTeleport =
    walkAnimation?.movementStyle === "teleport" &&
    Boolean(walkAnimation?.key) &&
    forwardFrameIndices.length > 1;

  if (canTeleport) {
    const reverseFrameIndices = [...forwardFrameIndices].reverse();

    return {
      style: "teleport",
      animation: walkAnimation,
      forwardFrameIndices,
      reverseFrameIndices,
      splitProgress: forwardFrameIndices.length / (forwardFrameIndices.length + reverseFrameIndices.length),
      totalDurationMs: getAnimationPlaybackDurationMs(
        forwardFrameIndices.length + reverseFrameIndices.length,
        walkAnimation.frameRate,
      ),
    };
  }

  const movementPhases = walkAnimation?.movementPhases ?? null;
  const phaseFrameIndices = movementPhases
    ? {
        start: getAnimationRangeFrameIndices(movementPhases.start),
        loop: getAnimationRangeFrameIndices(movementPhases.loop),
        end: getAnimationRangeFrameIndices(movementPhases.end),
      }
    : null;
  const hasPhasedPath =
    moveSegments > 0 &&
    Boolean(walkAnimation?.key) &&
    phaseFrameIndices &&
    phaseFrameIndices.start.length > 0 &&
    phaseFrameIndices.loop.length > 0 &&
    phaseFrameIndices.end.length > 0;

  if (hasPhasedPath) {
    const travelDurationMs = getBattleMoveDuration(moveSegments);
    const startDurationMs = getAnimationPlaybackDurationMs(
      phaseFrameIndices.start.length,
      walkAnimation.frameRate,
    );
    const endDurationMs = getAnimationPlaybackDurationMs(
      phaseFrameIndices.end.length,
      walkAnimation.frameRate,
    );
    const directionalFrameIndices = Object.fromEntries(
      Object.entries(walkAnimation.ranges ?? {})
        .filter(
          ([rangeName, range]) =>
            rangeName !== "default" &&
            (range.start !== defaultRange?.start || range.end !== defaultRange?.end),
        )
        .map(([rangeName, range]) => [
          rangeName,
          getAnimationRangeFrameIndices(range),
        ]),
    );

    return {
      style: "phased-path",
      animation: walkAnimation,
      forwardFrameIndices,
      reverseFrameIndices: [],
      splitProgress: 1,
      travelDurationMs,
      totalDurationMs: travelDurationMs + endDurationMs,
      directionalFrameIndices,
      phases: {
        start: {
          frameIndices: phaseFrameIndices.start,
          durationMs: startDurationMs,
        },
        loop: {
          frameIndices: phaseFrameIndices.loop,
        },
        end: {
          frameIndices: phaseFrameIndices.end,
          durationMs: endDurationMs,
        },
      },
    };
  }

  const travelDurationMs = getBattleMoveDuration(moveSegments);

  return {
    style: "path",
    animation: walkAnimation,
    forwardFrameIndices,
    reverseFrameIndices: [],
    splitProgress: 1,
    travelDurationMs,
    totalDurationMs: travelDurationMs,
  };
}
